import { CalendarShift, Job } from '@core/app/types/logworkApiType'
import { FormattedJob, FormattedJobsByDay } from '@core/utils/helpers/jobCalculations'
import { filterJobsByConditions, getConditionsByTabType } from '@core/utils/helpers/jobConditions'
import { groupShiftsByDay } from '@core/utils/helpers/shiftHelpers'

import { DayjsType } from '@page/logwork/logwork'
import { LogWorkTabType } from '@page/logwork/types/types'

export type JobIdsByDay = {
    [dayIndex: number | string]: number
}

/**
 * Sort jobIds keys properly (main days first, then sub-days)
 */
export const sortJobIdsKeys = (jobIds: JobIdsByDay): JobIdsByDay => {
    const sortedKeys = Object.keys(jobIds).sort((a, b) => {
        // Split keys into main day and sub-day parts
        const aParts = a.split('-')
        const bParts = b.split('-')

        const aMainDay = parseInt(aParts[0], 10)
        const bMainDay = parseInt(bParts[0], 10)

        // First sort by main day
        if (aMainDay !== bMainDay) {
            return aMainDay - bMainDay
        }

        // If same main day, sort by sub-day (if exists)
        const aSubDay = aParts.length > 1 ? parseInt(aParts[1], 10) : 0
        const bSubDay = bParts.length > 1 ? parseInt(bParts[1], 10) : 0

        return aSubDay - bSubDay
    })

    // Create new object with sorted keys
    const sortedJobIds: JobIdsByDay = {}
    sortedKeys.forEach((key) => {
        sortedJobIds[key] = jobIds[key]
    })

    return sortedJobIds
}

/**
 * Calculate job IDs for a specific tab type
 */
export const calculateJobIdsForTab = (
    jobs: Job[],
    shifts: CalendarShift[],
    dates: { start: DayjsType; end: DayjsType },
    formattedJobs: FormattedJobsByDay,
    availableJobs: FormattedJobsByDay,
    tabType: LogWorkTabType
): JobIdsByDay => {
    const newJobIds: JobIdsByDay = {}
    const conditions = getConditionsByTabType(tabType)
    const jobsInTab = filterJobsByConditions(jobs, conditions)

    // Group shifts by day
    const shiftsByDay = groupShiftsByDay(shifts, dates)

    for (let i = 0; i < 7; i++) {
        const formattedJobsForDay = formattedJobs[i] || []
        const availableJobsForDay = availableJobs[i] || []
        const dayShifts = shiftsByDay[i] || []

        // If there are multiple shifts for this day, create sub-day entries
        if (dayShifts.length > 1) {
            dayShifts.forEach((shift, shiftIndex) => {
                const availableJob = availableJobsForDay.find((job) => job.value === shift.jobId.toString())

                if (availableJob) {
                    if (shiftIndex === 0) {
                        // First shift goes to main day key
                        newJobIds[i] = Number(availableJob.value)
                    } else {
                        // Additional shifts go to sub-day keys
                        newJobIds[`${i}-${shiftIndex}`] = Number(availableJob.value)
                    }
                } else {
                    // If no available job found for this shift, try to find a suitable job
                    let primaryJob: Job | undefined | null = jobsInTab.find((j) => j.isPrimary) || null

                    if (!primaryJob && jobsInTab.length === 1) {
                        primaryJob = jobsInTab[0]
                    }

                    if (
                        primaryJob &&
                        formattedJobsForDay.some((fJob: FormattedJob) => fJob.value === primaryJob?.jobId.toString())
                    ) {
                        if (shiftIndex === 0) {
                            newJobIds[i] = primaryJob.jobId
                        } else {
                            newJobIds[`${i}-${shiftIndex}`] = primaryJob.jobId
                        }
                    } else {
                        if (shiftIndex === 0) {
                            newJobIds[i] = 0
                        } else {
                            newJobIds[`${i}-${shiftIndex}`] = 0
                        }
                    }
                }
            })
        } else if (dayShifts.length === 1) {
            // Single shift for the day - use existing logic
            const shift = dayShifts[0]
            const availableJob = availableJobsForDay.find((job) => job.value === shift.jobId.toString())

            let primaryJob: Job | undefined | null = jobsInTab.find((j) => j.isPrimary) || null

            if (availableJob) {
                newJobIds[i] = Number(availableJob.value)
                continue
            }

            if (
                primaryJob &&
                formattedJobsForDay.some((fJob: FormattedJob) => fJob.value === primaryJob?.jobId.toString()) &&
                formattedJobsForDay.find((j: FormattedJob) => +j.value === primaryJob?.jobId)?.isOpenedForDays[i]
            ) {
                newJobIds[i] = primaryJob.jobId
                continue
            }

            // If no primary job is found, check for a single available job
            if (!primaryJob && availableJobsForDay.length === 1) {
                primaryJob = jobsInTab.find((j) => j.jobId === Number(availableJobsForDay[0].value))
            }

            // If no primary job still, set the first job from the available ones
            if (!primaryJob && jobsInTab.length === 1) {
                primaryJob = jobsInTab[0]
            }

            if (
                primaryJob &&
                formattedJobsForDay.some((fJob: FormattedJob) => fJob.value === primaryJob?.jobId.toString()) &&
                formattedJobsForDay.find((j: FormattedJob) => +j.value === primaryJob?.jobId)?.isOpenedForDays[i]
            ) {
                newJobIds[i] = primaryJob.jobId
            } else if (availableJobsForDay.length === 1) {
                const selectedJob = jobsInTab.find((j) => j.jobId === Number(availableJobsForDay[0].value))
                if (selectedJob) {
                    newJobIds[i] = selectedJob.jobId
                } else {
                    newJobIds[i] = 0
                }
            } else {
                newJobIds[i] = 0
            }
        } else {
            // No shifts for this day - use existing fallback logic
            const primaryJob: Job | undefined | null = jobsInTab.find((j) => j.isPrimary) || null

            if (
                primaryJob &&
                formattedJobsForDay.some((fJob: FormattedJob) => fJob.value === primaryJob?.jobId.toString()) &&
                formattedJobsForDay.find((j: FormattedJob) => +j.value === primaryJob?.jobId)?.isOpenedForDays[i]
            ) {
                newJobIds[i] = primaryJob.jobId
            } else if (availableJobsForDay.length === 1) {
                const selectedJob = jobsInTab.find((j) => j.jobId === Number(availableJobsForDay[0].value))
                if (selectedJob) {
                    newJobIds[i] = selectedJob.jobId
                } else {
                    newJobIds[i] = 0
                }
            } else {
                newJobIds[i] = 0
            }
        }
    }

    return sortJobIdsKeys(newJobIds)
}
